"use client";

// In-session store for the global feedback widget — same Map/listener +
// useSyncExternalStore-compatible pattern as ironside-script-store.ts.
// No backend: entries live in memory and reset on reload, same tradeoff
// as the rest of this prototype's stage/upload tracking.

import { persist, isConfirmed } from "./store-persist";
import { sameData } from "./same-data";

export interface FeedbackEntry {
  id: string;
  path: string;
  stars: number; // 1-5
  message: string;
  submittedAt: string; // display string
  createdAt: number; // epoch ms — sortable across a reload (submittedAt is display-only)
}

const entries: FeedbackEntry[] = [];
const listeners = new Set<() => void>();
let version = 0;

export function submitFeedback(path: string, stars: number, message: string): FeedbackEntry {
  const entry: FeedbackEntry = {
    id: `fb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, // collision-safe across reloads
    path,
    stars,
    message: message.trim(),
    submittedAt: new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    createdAt: Date.now(),
  };
  entries.unshift(entry);
  persist("feedback", entry.id, entry);
  version++;
  listeners.forEach((l) => l());
  return entry;
}

export function getFeedbackEntries(): FeedbackEntry[] {
  return entries;
}

export function subscribeFeedback(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFeedbackVersion(): number {
  return version;
}

// ─── Hydration from the server ───────────────────────────────────────────────
// Array-backed: upsert by id, then re-sort newest-first by createdAt. Skip keys
// with an unconfirmed local write; bump version only when a row's content
// changed (lib/same-data.ts), so an idle poll notifies nobody. A key deleted on
// the server leaves the list.
export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  for (const d of deleted) {
    if (d.kind !== "feedback" || !isConfirmed(d.kind, d.id, null)) continue;
    const idx = entries.findIndex((e) => e.id === d.id);
    if (idx >= 0) {
      entries.splice(idx, 1);
      changed = true;
    }
  }
  for (const r of rows) {
    if (r.kind !== "feedback") continue;
    if (!isConfirmed(r.kind, r.id, r.data)) continue;
    const incoming = r.data as FeedbackEntry;
    const idx = entries.findIndex((e) => e.id === incoming.id);
    if (idx >= 0 && sameData(entries[idx], incoming)) continue; // nothing new: no re-sort, no notify
    if (idx >= 0) entries[idx] = incoming;
    else entries.push(incoming);
    changed = true;
  }
  if (changed) {
    entries.sort((a, b) => b.createdAt - a.createdAt); // newest first
    version++;
    listeners.forEach((l) => l());
  }
}
